import { Injectable } from '@angular/core';
import { HubConnection, HubConnectionBuilder, LogLevel, HubConnectionState } from '@microsoft/signalr';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { TokenStorageService } from './token-storage.service';
import { MediaAttachment } from '../models/MediaAttachment';
import { signalrMessageModel } from '../models/signalr-message.model';
import { HttpClient } from '@angular/common/http';
import { ConversationHistory, ConversationMessage } from '../models/conversation-history.model';
import { environment } from '../environments/environment';

@Injectable({ providedIn: 'root' })
export class ChatService {
  private connection: HubConnection | null = null;
  private accessToken: string = '';
  private messageSubject = new BehaviorSubject<signalrMessageModel | null>(null);
  public messages$ = this.messageSubject.asObservable();

  constructor(
    private _tokenStorage: TokenStorageService,
    private _http: HttpClient
  ) {}

  private debugLog(area: string, message: string, data: any = null) {
    const debugMessage = `[SignalR ${area}] ${message}`;
    console.log(debugMessage, data || '');
  }

  setAccessToken(accessToken: string) {
    this.accessToken = accessToken;
  }

  //Create SignalR connection
  private async createConnection(): Promise<HubConnection> {
    this.debugLog('Config', 'Creating connection');
    const response = await fetch(`${environment.apiUrl}/chat/negotiate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'RoarChatAuth': this.accessToken || ''
      }
    });
    if (!response.ok) throw new Error('Failed to get connection info');
    const negotiate = await response.json();

    const connection = new HubConnectionBuilder()
      .withUrl(negotiate.url, {
        accessTokenFactory: () => negotiate.accessToken
      })
      .configureLogging(LogLevel.Debug)
      .withAutomaticReconnect([0, 2000, 10000])
      .build();

    connection.onreconnecting(error => this.debugLog('Connection', 'Reconnecting', error));
    connection.onreconnected(id => this.debugLog('Connection', 'Reconnected', id));
    connection.onclose(error => {
      this.debugLog('Connection', 'Closed', error);
      this.connection = null;
    });

    return connection;
  }

  //Setup SignalR handlers
  private setupHandlers(connection: HubConnection) {
    connection.on('broadcastMessage', message => {
      this.debugLog('Message', 'Broadcast received');
    });

    connection.on('message', (message: signalrMessageModel) => {
      this.debugLog('Message', 'Message received');
      this.messageSubject.next(message);
    });
  }

  //Initialize SignalR
  public async startConnection(): Promise<void> {
    try {
      if (!this.connection) {
        this.connection = await this.createConnection();
        this.setupHandlers(this.connection);
      }

      if (this.connection.state === HubConnectionState.Disconnected) {
        await this.connection.start();
        this.debugLog('Connection', 'Connected');
      }
    } catch (err) {
      this.debugLog('Error', 'Connection error', err);
      this.connection = null;
      setTimeout(() => this.startConnection(), 5000);
    }
  }

  joinConversation(organizationId: string, conversationId: string, joinCode: string): Observable<string> {
    const payload = {
      "ConversationId": conversationId,
      "OrganizationId": organizationId,
      "JoinCode": joinCode
    };

    return this._http.post<any>(`${environment.apiUrl}/chat/join`, payload).pipe(
      map((data: any) => {
          this._tokenStorage.store(conversationId, data.accessToken, data.refreshToken);
          return data.accessToken;
        })
      )
  }

  fetchConversation(): Observable<ConversationHistory> {
    return this._http.get<ConversationHistory>(
      `${environment.apiUrl}/chat/conversation`,
      {
        headers: {
          'Content-Type': 'application/json',
          'RoarChatAuth': this.accessToken
        }
      }
    );
  }

  fetchConversationHistory(): Observable<ConversationMessage[]>{
    return this.fetchConversation().pipe( map((data: ConversationHistory) => data.messages) );
  }

  public async sendMessage(messageText: string, mediaAttached: MediaAttachment[] | null = null): Promise<Observable<any>> {
    if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
      this.debugLog('Message', 'Connection not ready, reconnecting...');
      await this.startConnection();
    }

    const url = `${environment.apiUrl}/chat/message/advisor`;
    const payload = {
      MessageText: messageText,
      Media: mediaAttached
    };

    return this._http.post(url, payload, { 
      headers: {
        'Content-Type': 'application/json',
        'RoarChatAuth': this.accessToken || ''
      }
     });
  }

  public getConnectionState(): string {
    const state = this.connection ? HubConnectionState[this.connection.state] : 'No Connection';
    this.debugLog('State', 'Connection state', state);
    return state;
  }
}